/**
 * Utilities for handling clipboard content extraction.
 * Focused helpers for extracting images from the clipboard in a reusable way.
 */

/**
 * Infers a sensible filename for an image based on its MIME type.
 * Falls back to a generic name when the MIME type is not recognized.
 */
export const inferImageFileName = (mimeType: string): string => {
  if (mimeType === 'image/png') return 'pasted-image.png';
  if (mimeType === 'image/jpeg') return 'pasted-image.jpg';
  if (mimeType === 'image/webp') return 'pasted-image.webp';
  if (mimeType === 'image/gif') return 'pasted-image.gif';
  if (mimeType === 'image/bmp') return 'pasted-image.bmp';
  if (mimeType === 'image/svg+xml') return 'pasted-image.svg';
  return 'pasted-image';
};

/**
 * Extracts the first image file from a clipboard data payload, if present.
 * Does not call preventDefault; callers should do that when appropriate.
 *
 * @param clipboardData - The event's clipboardData object
 * @returns A File object for the pasted image, or null if none was found
 */
export const getPastedImageFile = (
  clipboardData: DataTransfer | null | undefined
): File | null => {
  if (!clipboardData) return null;

  const items = clipboardData.items;
  if (!items || items.length === 0) return null;

  for (const item of items) {
    const isFileItem = item.kind === 'file';
    if (!isFileItem) continue;

    const blob = item.getAsFile();
    if (!blob) continue;

    const mimeType = blob.type || 'image/png';
    const isImageFile = mimeType.startsWith('image/');
    if (!isImageFile) continue;

    const blobAsFile = blob as File;
    const hasValidFileName =
      blob instanceof File &&
      typeof blobAsFile.name === 'string' &&
      blobAsFile.name;

    const file = hasValidFileName
      ? blobAsFile
      : new File([blob], inferImageFileName(mimeType), { type: mimeType });

    return file;
  }

  return null;
};
